import PieChartOutlineOutlinedIcon from '@mui/icons-material/PieChartOutlineOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { Box } from '@mui/material';
import { ISpeciesCountData, ISpeciesTable } from 'common/defines/analytic';
import { QUERY_KEY } from 'constants/constants';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { getAllSpeciesCountAnalytic } from 'services/analytics/apiAnalyticsConfig.services';
import { useAppSelector } from 'store/hooks';
import { mapViewSelector } from 'store/slices/mapViewSlice';
import { TYPE_ANALYTICS_MAP_VIEW } from '../../../../../../common/defines/constants';
import { SpeciesCountCardLayer } from '../../Layer/SpeciesCount';
import { SwipeableViewsCustom } from '../../SwipeableViewsCustom';
import ChartSpeciesCount from './ChartSpeciesCount';
import TableSpeciesCount from './TableSpeciesCount';

export const SpeciesCountAnalytics = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalDocs, setTotalDocs] = useState<number>(1);
  const [speciesCountData, setSpeciesCountData] = useState<ISpeciesCountData[]>([]);
  const [isShowTable, setIsShowTable] = useState<boolean>(true);

  const { analyticId } = useAppSelector(mapViewSelector);

  useQuery(
    [QUERY_KEY.GET_All_SPECIES_COUNT, currentPage],
    () =>
      getAllSpeciesCountAnalytic({
        analysisId: analyticId!,
        page: currentPage,
        perPage: 500,
      }),
    {
      enabled: !!analyticId,
      onSuccess(data) {
        const hasNextPage = data.hasNextPage;
        const docs = data.docs;
        if (hasNextPage) {
          setCurrentPage((prev) => prev + 1);
        }
        setSpeciesCountData((prev) => {
          if (currentPage === 1) return docs;
          else return [...prev, ...docs];
        });
        setTotalDocs(data.totalDocs);
      },
    }
  );

  // reset data when changing analytic
  useEffect(() => {
    setSpeciesCountData([]);
    setCurrentPage(1);
  }, [analyticId]);

  const finalSpeciesCountTable = useMemo(() => {
    const groupedSpeciesCountData = speciesCountData.reduce<ISpeciesTable[]>((acc, cur) => {
      const foundSpecies = acc.find((item) => item.speciesId === cur.speciesId);
      if (foundSpecies) {
        foundSpecies.count += 1;
      } else {
        acc.push({
          speciesId: cur.speciesId,
          name: cur.name,
          sequence: cur.sequence,
          defaultColor: cur.defaultColor,
          settingColor: cur.settingColor,
          count: 1,
          percentage: (1 / totalDocs) * 100,
        });
      }
      return acc;
    }, []);

    const sortedSpeciesCountTable = [...groupedSpeciesCountData].sort((a, b) => a.sequence - b.sequence);
    return sortedSpeciesCountTable.map((item) => ({
      ...item,
      percentage: (item.count / totalDocs) * 100,
    }));
  }, [speciesCountData, totalDocs]);

  return (
    <SwipeableViewsCustom
      analyticName={TYPE_ANALYTICS_MAP_VIEW.SPECIES_COUNT}
      analyticTab={
        <Box sx={{ p: '16px' }}>
          <Box sx={{ textAlign: 'right' }}>
            <Box onClick={() => setIsShowTable((prev) => !prev)} sx={{ cursor: 'pointer' }}>
              {isShowTable ? <TableChartOutlinedIcon /> : <PieChartOutlineOutlinedIcon />}
            </Box>
          </Box>
          {isShowTable ? (
            <TableSpeciesCount tableData={finalSpeciesCountTable} />
          ) : (
            <ChartSpeciesCount chartData={finalSpeciesCountTable} />
          )}
        </Box>
      }
      layerTab={
        <SpeciesCountCardLayer
          labelCard={TYPE_ANALYTICS_MAP_VIEW.SPECIES_COUNT}
          layerList={finalSpeciesCountTable}
          setCurrentPage={setCurrentPage}
        />
      }
      isShowDownloadButton={false}
    />
  );
};
